import { useEffect, useRef, useState } from "react";
import { MerchantButton } from "@components";
import {
  PageTemplate,
  PageBody,
  PageFooter,
  Summary,
} from "../shared-components";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  InvoiceGroup,
  InvoiceItem,
  CreditNoteItem,
  InvoiceDetailsModal,
  DisputeModal,
} from "./components";
import { usePostHog } from "posthog-js/react";
import { useDisputes, useEarlySettlementDiscount } from "@flags";
import { formatAmount } from "@utils";

const PRSelectInvoices = () => {
  const {
    id,
    merchant,
    statement,
    customerId,
    setSelectedInvoices,
    summaryData,
    settlementDiscount,
    selectedInvoiceIds,
    toggleInvoice,
    toggleGroup,
    flowId,
    refreshPaymentRequest,
  } = useOutletContext();

  const navigate = useNavigate();
  const disputesEnabled = useDisputes();
  const earlySettlementEnabled = useEarlySettlementDiscount();

  const [selectedInvoiceDetails, setSelectedInvoiceDetails] = useState(null);
  // Ledger transaction ID of the row whose details modal is currently open.
  // Captured at open-time so the line-item dispute handler has it available.
  const [selectedRowLedgerTransactionId, setSelectedRowLedgerTransactionId] = useState(null);
  const [isInvoiceDetailsModalOpen, setIsInvoiceDetailsModalOpen] =
    useState(false);

  // disputeTarget shape: { ledgerTransactionId, invoiceNumber, lineItems } | null
  const [disputeTarget, setDisputeTarget] = useState(null);
  // Holds the last non-null target so modal content doesn't change during close animation
  const lastDisputeTargetRef = useRef(null);
  if (disputeTarget) lastDisputeTargetRef.current = disputeTarget;

  // Track when the statement is opened
  const posthog = usePostHog();
  useEffect(() => {
    if (!posthog || !id) return;
    posthog.capture("statement_opened", {
      paymentRequestId: id,
    });
  }, [posthog, id]);

  const onContinue = async () => {
    await setSelectedInvoices(
      Math.max(0, summaryData.totalToPay - settlementDiscount),
      Array.from(selectedInvoiceIds)
    );
    navigate(`/${flowId}/${id}/paymentmethod`);
  };

  // Called when line items are selected for dispute from inside the invoice details modal
  const handleDisputeLineItems = (lineItems) => {
    if (!selectedInvoiceDetails || !selectedRowLedgerTransactionId) return;
    setIsInvoiceDetailsModalOpen(false);
    setDisputeTarget({
      ledgerTransactionId: selectedRowLedgerTransactionId,
      invoiceNumber: selectedInvoiceDetails.number,
      lineItems,
    });
  };

  // Show loading state if data isn't ready yet
  if (!selectedInvoiceIds || !summaryData) {
    return <div>Loading...</div>;
  }

  return (
    <PageTemplate>
      <PageBody>
        <div className="px-5 lg:px-0 pb-5">
          <h2 className="text-xl/8 font-medium">Select invoices to pay</h2>
          <p className="text-sm text-zinc-600 font-light">
            Choose the invoices you wish to settle today
          </p>
        </div>
        <div className="px-5 lg:px-0 flex flex-col gap-3">
          {statement.invoiceGroups
            .filter((g) => statement.allowAdditionalInvoices || g.required)
            .map((group) => {
              const { required, invoices, creditNotes, name } = group;

              const ids = [
                ...invoices.filter((i) => !i.isDisputed).map((i) => i.ledgerTransactionId),
                ...creditNotes.map((cn) => cn.ledgerTransactionId),
              ];
              const selectedIds = ids.filter((id) =>
                selectedInvoiceIds?.has(id)
              );

              const allInvoicesDisputed = invoices.length > 0 && invoices.every((i) => i.isDisputed);

              const discountedSelectedCount = earlySettlementEnabled
                ? invoices.filter(
                    (inv) =>
                      selectedInvoiceIds.has(inv.ledgerTransactionId) &&
                      inv.discountAmount > 0
                  ).length
                : 0;

              return (
                <InvoiceGroup
                  key={name}
                  group={group}
                  selectedIds={selectedIds}
                  toggleGroup={toggleGroup}
                  ids={ids}
                  invoicesRequestedAreMandatory={
                    statement.invoicesRequestedAreMandatory
                  }
                  allInvoicesDisputed={allInvoicesDisputed}
                  discountedSelectedCount={discountedSelectedCount}
                >
                  {creditNotes.map((cn) => (
                    <CreditNoteItem
                      key={cn.ledgerTransactionId}
                      required={
                        statement.invoicesRequestedAreMandatory && required
                      }
                      creditNote={cn}
                      selected={selectedInvoiceIds}
                      toggleInvoice={toggleInvoice}
                    />
                  ))}
                  {invoices.map((inv) => (
                    <InvoiceItem
                      key={inv.ledgerTransactionId}
                      required={
                        statement.invoicesRequestedAreMandatory && required
                      }
                      inv={inv}
                      selected={selectedInvoiceIds}
                      toggleInvoice={toggleInvoice}
                      setSelectedInvoiceDetails={setSelectedInvoiceDetails}
                      setSelectedRowLedgerTransactionId={setSelectedRowLedgerTransactionId}
                      customerId={customerId}
                      setIsInvoiceDetailsModalOpen={
                        setIsInvoiceDetailsModalOpen
                      }
                      setDisputeTarget={setDisputeTarget}
                    />
                  ))}
                </InvoiceGroup>
              );
            })}
        </div>
      </PageBody>

      <PageFooter>
        <div className="pb-7 space-y-2 hidden lg:block">
          <h2 className="text-xl font-medium">Payment summary</h2>
        </div>

        <Summary
          invoiceTotal={summaryData.invoiceTotal}
          invoiceCount={summaryData.invoiceCount}
          creditNoteTotal={summaryData.creditNoteTotal}
          creditNoteCount={summaryData.creditNoteCount}
          totalToPay={Math.max(0, summaryData.totalToPay - settlementDiscount)}
          creditOnAccount={summaryData.creditOnAccount}
          hasStatement={!!statement}
          amountRequested={statement?.amountRequested ?? summaryData.totalToPay}
          settlementDiscount={settlementDiscount}
        />

        <MerchantButton
          className="w-full mt-4 lg:mt-6"
          onClick={onContinue}
          merchant={merchant}
          disabled={!selectedInvoiceIds?.size || summaryData.totalToPay < 0.3}
        >
          Confirm and continue
        </MerchantButton>

        {earlySettlementEnabled && settlementDiscount > 0 && (
          <p className="text-center text-xs text-green-700 mt-2">
            You&apos;re saving {formatAmount(settlementDiscount)} by settling today
          </p>
        )}
      </PageFooter>

      <InvoiceDetailsModal
        invoice={selectedInvoiceDetails}
        isOpen={isInvoiceDetailsModalOpen}
        onClose={() => {
          setIsInvoiceDetailsModalOpen(false);
          setSelectedRowLedgerTransactionId(null);
        }}
        orgId={merchant?.organisationId}
        onDisputeLineItems={disputesEnabled && !selectedInvoiceDetails?.isDisputed ? handleDisputeLineItems : undefined}
      />

      {disputesEnabled && <DisputeModal
        isOpen={!!disputeTarget}
        onClose={() => { setDisputeTarget(null); setSelectedInvoiceDetails(null); setSelectedRowLedgerTransactionId(null); }}
        onSuccess={() => { setDisputeTarget(null); setSelectedInvoiceDetails(null); setSelectedRowLedgerTransactionId(null); }}
        ledgerTransactionId={lastDisputeTargetRef.current?.ledgerTransactionId}
        lineItems={lastDisputeTargetRef.current?.lineItems}
        invoiceNumber={lastDisputeTargetRef.current?.invoiceNumber}
        merchant={merchant}
        onRefreshStatement={refreshPaymentRequest}
      />}
    </PageTemplate>
  );
};

export default PRSelectInvoices;
